import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { ClassroomDetail, Quiz, QuizQuestion } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { toLocalInput } from '../calendar/dates';
import { shortDate } from './shared';

type Run = (action: () => Promise<unknown>, ok?: string) => Promise<boolean>;
type Draft = Omit<QuizQuestion, 'id'> & { id?: string };
const KIND = { cuestionario: 'Cuestionario', examen: 'Examen' } as const;
/** Cierto o falso es una opción múltiple con estas dos opciones (se califica sola). */
const TRUE_FALSE = ['Cierto', 'Falso'] as const;
const isTrueFalse = (q: Draft) => q.type === 'choice' && q.options.length === 2 && q.options[0] === TRUE_FALSE[0] && q.options[1] === TRUE_FALSE[1];

/** Cuestionarios y exámenes del salón. */
export function QuizzesTab({ c, base, run, busy }: { c: ClassroomDetail; base: string; run: Run; busy: boolean }) {
  const [editing, setEditing] = useState<Quiz | 'new' | null>(null);
  if (editing) return <QuizEditor quiz={editing === 'new' ? null : editing} sessions={c.sessions} quizzes={c.quizzes} base={base} run={run} busy={busy} onDone={() => setEditing(null)} />;
  return (
    <>
      {c.canTeach ? (
        <div className="actions">
          <Button onClick={() => setEditing('new')}>Nuevo cuestionario o examen</Button>
        </div>
      ) : null}
      {c.quizzes.length === 0 ? (
        <Card>
          <p className="muted">No hay cuestionarios.</p>
        </Card>
      ) : null}
      {c.quizzes.map((q) => {
        const session = q.sessionId ? c.sessions.find((x) => x.id === q.sessionId) : undefined;
        return (
          <QuizCard
            key={q.id}
            quiz={q}
            canTeach={c.canTeach}
            base={base}
            run={run}
            busy={busy}
            onEdit={() => setEditing(q)}
            session={session ? { to: `/app/salones/${c.id}/clases/${session.id}`, label: `${session.topic?.title ?? 'Clase'} · ${shortDate(session.startsAt)}` } : null}
          />
        );
      })}
    </>
  );
}

/** Un cuestionario: el maestro ve los resultados; el alumno lo contesta o revisa su entrega. */
export function QuizCard({
  quiz: q,
  canTeach,
  base,
  run,
  busy,
  onEdit,
  session,
}: {
  quiz: Quiz;
  canTeach: boolean;
  base: string;
  run: Run;
  busy: boolean;
  onEdit?: () => void;
  session?: { to: string; label: string } | null;
}) {
  const [params] = useSearchParams();
  const linked = params.get('cuestionario') === q.id;
  const [open, setOpen] = useState(linked);
  const ref = useRef<HTMLDivElement>(null);
  // Desde un enlace compartido: el cuestionario se abre y se lleva a la vista.
  useEffect(() => {
    if (linked) ref.current?.scrollIntoView?.({ block: 'start' });
  }, [linked]);
  return (
    <div ref={ref} id={`cuestionario-${q.id}`}>
      <Card
        title={`${KIND[q.kind]}: ${q.title}`}
        action={
          canTeach && onEdit ? (
            <Button variant="ghost" onClick={onEdit}>
              Editar
            </Button>
          ) : null
        }
      >
        {session ? (
          <p className="small">
            Clase: <Link to={session.to}>{session.label}</Link>
          </p>
        ) : null}
        <p className="small muted">
          {q.questions.length} {q.questions.length === 1 ? 'pregunta' : 'preguntas'}
          {!q.published ? ' · Borrador (los alumnos no lo ven)' : q.open ? ' · Abierto' : ' · Cerrado'}
          {q.closesAt ? ` · Cierra ${new Date(q.closesAt).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
        </p>
        {q.instructions ? <p className="pre-line">{q.instructions}</p> : null}
        {canTeach ? <QuizTools quiz={q} base={base} /> : null}
        {canTeach ? (
          <Results quiz={q} base={base} run={run} busy={busy} />
        ) : q.mySubmission ? (
          <Review quiz={q} />
        ) : q.open ? (
          open ? (
            <Take quiz={q} base={base} run={run} busy={busy} />
          ) : (
            <Button onClick={() => setOpen(true)}>Contestar</Button>
          )
        ) : (
          <p className="muted">Ya cerró.</p>
        )}
      </Card>
    </div>
  );
}

/** Enlace para el alumno: entra con su cuenta y llega directo a contestarlo. */
export function quizLink(base: string, q: Quiz): string {
  const room = base.split('/').pop();
  const path = q.sessionId ? `/app/salones/${room}/clases/${q.sessionId}?cuestionario=${q.id}` : `/app/salones/${room}?ver=temario&cuestionario=${q.id}`;
  return `${window.location.origin}${path}`;
}

/** PDF para alumnos o con respuestas, y compartir el enlace (solo quien enseña). */
function QuizTools({ quiz: q, base }: { quiz: Quiz; base: string }) {
  const [shared, setShared] = useState<'copied' | 'draft' | 'manual' | null>(null);
  const print = `/app/salones/${base.split('/').pop()}/cuestionarios/${q.id}/imprimir`;
  const link = quizLink(base, q);
  const message = `Contesta el cuestionario «${q.title}» en KEORA: ${link}`;

  async function share() {
    if (!q.published) {
      setShared('draft');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setShared('copied');
    } catch {
      setShared('manual');
    }
  }

  return (
    <>
      <div className="actions">
        <a className="btn btn--ghost" href={print} target="_blank" rel="noopener noreferrer">
          PDF para alumnos
        </a>
        <a className="btn btn--ghost" href={`${print}?respuestas=1`} target="_blank" rel="noopener noreferrer">
          PDF con respuestas
        </a>
        <Button variant="ghost" onClick={() => void share()}>
          Compartir enlace
        </Button>
      </div>
      {shared === 'draft' ? <Alert tone="info">Es un borrador: publícalo primero («Editar» → «Publicar») para que los alumnos lo puedan abrir.</Alert> : null}
      {shared === 'copied' || shared === 'manual' ? (
        <Alert tone="success">
          {shared === 'copied' ? 'Enlace copiado. ' : <span className="break-all">Copia este enlace: {link} </span>}
          <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer">
            Enviar por WhatsApp
          </a>
          . Cada alumno entra con su cuenta y llega directo a contestarlo.
        </Alert>
      ) : null}
    </>
  );
}

function Results({ quiz: q, base, run, busy }: { quiz: Quiz; base: string; run: Run; busy: boolean }) {
  const results = q.results ?? [];
  const [show, setShow] = useState<string | null>(null);
  const hasOpen = q.questions.some((x) => x.type === 'text');
  if (results.length === 0) return <p className="small muted">Nadie ha contestado todavía.</p>;
  const pending = (r: (typeof results)[number]) => q.questions.filter((x) => x.type === 'text' && r.openGrades?.[x.id] === undefined).length;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Alumno</th>
            <th>Calificación</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.person.personId}>
              <td>{r.person.name}</td>
              <td>
                {r.maxScore ? `${r.score} / ${r.maxScore}` : '—'}
                {hasOpen && pending(r) > 0 ? <span className="small muted block">{pending(r) === 1 ? '1 abierta por calificar' : `${pending(r)} abiertas por calificar`}</span> : null}
              </td>
              <td>
                <Button variant="ghost" onClick={() => setShow(show === r.person.personId ? null : r.person.personId)}>
                  {show === r.person.personId ? 'Ocultar' : hasOpen ? 'Ver y calificar' : 'Ver respuestas'}
                </Button>
                {show === r.person.personId ? (
                  <form
                    className="small"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      const grades: Record<string, number | null> = {};
                      for (const x of q.questions.filter((x) => x.type === 'text')) {
                        const v = String(f.get(`grade-${x.id}`) ?? '').trim();
                        grades[x.id] = v === '' ? null : Number(v);
                      }
                      void run(() => api.put(`${base}/quizzes/${q.id}/submissions/${r.person.personId}/grades`, { grades }), 'Calificación guardada.');
                    }}
                  >
                    <ol>
                      {q.questions.map((qq, i) => (
                        <li key={qq.id}>
                          {qq.text}: <strong>{answerText(qq, r.answers[i])}</strong> {qq.type === 'choice' ? (r.answers[i] === qq.correct ? '✔' : '✘') : ''}
                          {qq.type === 'text' ? (
                            <label className="quiz-grade">
                              Puntos (0 a {qq.points}){' '}
                              <input name={`grade-${qq.id}`} type="number" min={0} max={qq.points} step="0.5" defaultValue={r.openGrades?.[qq.id] ?? ''} aria-label={`Puntos de «${qq.text}» para ${r.person.name}`} />
                            </label>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                    {hasOpen ? (
                      <Button type="submit" variant="secondary" busy={busy}>
                        Guardar calificación
                      </Button>
                    ) : null}
                  </form>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const answerText = (q: QuizQuestion, a: number | string | null | undefined) =>
  a === null || a === undefined || a === '' ? 'Sin respuesta' : q.type === 'choice' && typeof a === 'number' ? (q.options[a] ?? '—') : String(a);

function Review({ quiz: q }: { quiz: Quiz }) {
  const s = q.mySubmission!;
  return (
    <>
      {s.maxScore ? (
        <Alert tone="success">
          Tu calificación: {s.score} de {s.maxScore}
          {q.questions.some((x) => x.type === 'text' && s.openGrades?.[x.id] === undefined) ? ' (faltan por calificar tus respuestas abiertas; suman cuando tu maestro las revise)' : ''}
        </Alert>
      ) : (
        <Alert tone="success">Ya entregaste. Tu maestro revisará tus respuestas.</Alert>
      )}
      <ol className="quiz-review">
        {q.questions.map((x, i) => (
          <li key={x.id}>
            <p>{x.text}</p>
            <p className="small">
              Tu respuesta: <strong>{answerText(x, s.answers[i])}</strong>
              {x.type === 'choice' && x.correct !== null ? (s.answers[i] === x.correct ? ' ✔' : ` ✘ · Correcta: ${x.options[x.correct]}`) : ''}
              {x.type === 'text' && s.openGrades?.[x.id] !== undefined ? ` · ${s.openGrades[x.id]} de ${x.points} puntos` : ''}
            </p>
          </li>
        ))}
      </ol>
    </>
  );
}

function Take({ quiz: q, base, run, busy }: { quiz: Quiz; base: string; run: Run; busy: boolean }) {
  const [answers, setAnswers] = useState<(number | string | null)[]>(q.questions.map(() => null));
  const missing = q.questions.some((x, i) => x.type === 'choice' && answers[i] === null);
  return (
    <form
      className="form quiz-take"
      onSubmit={(e) => {
        e.preventDefault();
        if (!missing && window.confirm('¿Entregar? Solo se puede contestar una vez.')) void run(() => api.post(`${base}/quizzes/${q.id}/submissions`, { answers }), '¡Entregado!');
      }}
    >
      {q.questions.map((x, i) => (
        <fieldset key={x.id} className="quiz-q">
          <legend>
            {i + 1}. {x.text} {x.points > 1 ? <span className="small muted">({x.points} puntos)</span> : null}
          </legend>
          {x.type === 'choice' ? (
            x.options.map((o, j) => (
              <label key={j} className="check quiz-option">
                <input type="radio" name={`q-${x.id}`} checked={answers[i] === j} onChange={() => setAnswers((a) => a.map((v, k) => (k === i ? j : v)))} /> {o}
              </label>
            ))
          ) : (
            <textarea aria-label={`Respuesta ${i + 1}`} rows={3} maxLength={4000} value={(answers[i] as string | null) ?? ''} onChange={(e) => setAnswers((a) => a.map((v, k) => (k === i ? e.target.value : v)))} />
          )}
        </fieldset>
      ))}
      <Button type="submit" busy={busy} disabled={missing}>
        Entregar
      </Button>
    </form>
  );
}

export function QuizEditor({
  quiz,
  sessions: allSessions,
  quizzes,
  base,
  run,
  busy,
  onDone,
  sessionId: initialSession,
}: {
  quiz: Quiz | null;
  /** Para uno nuevo desde la página de la clase: ya viene con esa clase elegida. */
  sessionId?: string;
  sessions: ClassroomDetail['sessions'];
  /** Los del salón: una clase que ya tiene cuestionario no se ofrece (uno por clase). */
  quizzes: Quiz[];
  base: string;
  run: Run;
  busy: boolean;
  onDone: () => void;
}) {
  const taken = new Set(quizzes.filter((x) => x.sessionId && x.id !== quiz?.id).map((x) => x.sessionId));
  const sessions = allSessions.filter((s) => !taken.has(s.id));
  const [kind, setKind] = useState<Quiz['kind']>(quiz?.kind ?? 'cuestionario');
  const [sessionId, setSessionId] = useState(quiz?.sessionId ?? initialSession ?? '');
  const [title, setTitle] = useState(quiz?.title ?? '');
  const [instructions, setInstructions] = useState(quiz?.instructions ?? '');
  const [closesAt, setClosesAt] = useState(quiz?.closesAt ? toLocalInput(new Date(quiz.closesAt)) : '');
  const [questions, setQuestions] = useState<Draft[]>(quiz?.questions ?? []);
  const set = (i: number, patch: Partial<Draft>) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  function save(published: boolean) {
    const body = { sessionId: sessionId || null, kind, title: title.trim(), instructions: instructions.trim() || null, closesAt: closesAt ? new Date(closesAt).toISOString() : null, published, questions };
    void run(() => (quiz ? api.put(`${base}/quizzes/${quiz.id}`, body) : api.post(`${base}/quizzes`, body)), published ? 'Publicado: los alumnos ya lo pueden contestar.' : 'Guardado como borrador.').then(
      (ok) => ok && onDone(),
    );
  }

  return (
    <Card title={quiz ? 'Editar' : 'Nuevo cuestionario o examen'}>
      <div className="form">
        <div className="segmented" role="radiogroup" aria-label="Tipo">
          {(['cuestionario', 'examen'] as const).map((k) => (
            <label key={k} className="check">
              <input type="radio" checked={kind === k} onChange={() => setKind(k)} /> {KIND[k]}
            </label>
          ))}
        </div>
        <Field label="Título" value={title} maxLength={150} onChange={(e) => setTitle(e.target.value)} placeholder="Repaso de Génesis" />
        <div className="field">
          <label htmlFor="quiz-instr">Instrucciones (opcional)</label>
          <textarea id="quiz-instr" rows={2} value={instructions} maxLength={2000} onChange={(e) => setInstructions(e.target.value)} />
        </div>
        {sessions.length > 0 ? (
          <div className="field">
            <label htmlFor="quiz-session">Clase</label>
            <select id="quiz-session" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              <option value="">Todo el salón</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.topic?.title ?? 'Clase'} · {shortDate(s.startsAt)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <Field label="Cierra (opcional)" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />

        {questions.map((q, i) => (
          <fieldset key={i} className="quiz-q quiz-q--edit">
            <legend>Pregunta {i + 1}</legend>
            <Field label={`Texto de la pregunta ${i + 1}`} value={q.text} maxLength={1000} onChange={(e) => set(i, { text: e.target.value })} />
            <div className="form__row">
              <div className="field">
                <label htmlFor={`qt-${i}`}>Tipo</label>
                <select
                  id={`qt-${i}`}
                  value={isTrueFalse(q) ? 'truefalse' : q.type}
                  onChange={(e) =>
                    set(
                      i,
                      e.target.value === 'truefalse'
                        ? { type: 'choice', options: [...TRUE_FALSE], correct: 0 }
                        : { type: e.target.value as Draft['type'], correct: e.target.value === 'choice' ? 0 : null, options: e.target.value === 'choice' ? (q.options.length && !isTrueFalse(q) ? q.options : ['', '']) : [] },
                    )
                  }
                >
                  <option value="choice">Opción múltiple</option>
                  <option value="truefalse">Cierto o falso</option>
                  <option value="text">Respuesta abierta</option>
                </select>
              </div>
              <Field label="Puntos" type="number" min={0} max={100} value={q.points} onChange={(e) => set(i, { points: Number(e.target.value) || 0 })} />
            </div>
            {q.type === 'choice' ? (
              <>
                {q.options.map((o, j) => (
                  <div key={j} className="quiz-option-edit">
                    <input type="radio" name={`correct-${i}`} aria-label={`Opción ${j + 1} es la correcta`} checked={q.correct === j} onChange={() => set(i, { correct: j })} />
                    <input aria-label={`Opción ${j + 1} de la pregunta ${i + 1}`} value={o} maxLength={300} onChange={(e) => set(i, { options: q.options.map((x, k) => (k === j ? e.target.value : x)) })} />
                    {q.options.length > 2 ? (
                      <button type="button" className="ev-chip__x" aria-label={`Quitar opción ${j + 1}`} onClick={() => set(i, { options: q.options.filter((_, k) => k !== j), correct: q.correct === j ? 0 : q.correct !== null && q.correct > j ? q.correct - 1 : q.correct })}>
                        ×
                      </button>
                    ) : null}
                  </div>
                ))}
                <p className="small muted">Marca el círculo de la respuesta correcta.</p>
                {q.options.length < 10 && !isTrueFalse(q) ? (
                  <Button type="button" variant="ghost" onClick={() => set(i, { options: [...q.options, ''] })}>
                    Agregar opción
                  </Button>
                ) : null}
              </>
            ) : null}
            <Button type="button" variant="ghost" onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))}>
              Quitar pregunta
            </Button>
          </fieldset>
        ))}
        <div className="actions">
          <Button type="button" variant="secondary" onClick={() => setQuestions((qs) => [...qs, { text: '', type: 'choice', options: ['', ''], correct: 0, points: 1 }])}>
            Agregar pregunta
          </Button>
        </div>
        <div className="actions">
          <Button type="button" busy={busy} disabled={!title.trim() || questions.length === 0} onClick={() => save(true)}>
            Publicar
          </Button>
          <Button type="button" variant="secondary" busy={busy} disabled={!title.trim()} onClick={() => save(false)}>
            Guardar borrador
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
          {quiz && quiz.canDelete !== false ? (
            <Button
              type="button"
              variant="danger"
              busy={busy}
              onClick={() => {
                if (window.confirm('¿Borrar este cuestionario y sus respuestas?')) void run(() => api.delete(`${base}/quizzes/${quiz.id}`), 'Cuestionario borrado.').then((ok) => ok && onDone());
              }}
            >
              Borrar
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
